import { supabase } from '@/infrastructure/supabase/client'
import { Site } from '@/domain/model/Site'
import type { ISiteRepository, CreateSiteInput } from '@/application/port/out/ISiteRepository'
import type { SiteSettings, Tables, TablesUpdate, Json } from '@brix/types'

function toEntity(row: Tables<'sites'>): Site {
  return Site.reconstitute({
    id: row.id,
    tenantId: row.tenant_id,
    name: row.name,
    subdomain: row.subdomain,
    customDomain: row.custom_domain,
    published: row.published,
    settings: (row.settings as unknown as SiteSettings) ?? {},
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  })
}

export class SupabaseSiteRepository implements ISiteRepository {
  async findById(id: string): Promise<Site | null> {
    const { data } = await supabase.from('sites').select('*').eq('id', id).single()
    return data ? toEntity(data) : null
  }

  async findByTenantId(tenantId: string): Promise<Site[]> {
    const { data } = await supabase
      .from('sites')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
    return (data ?? []).map(toEntity)
  }

  async findBySubdomain(subdomain: string): Promise<Site | null> {
    const { data } = await supabase.from('sites').select('*').eq('subdomain', subdomain).single()
    return data ? toEntity(data) : null
  }

  async create(input: CreateSiteInput): Promise<Site> {
    const { data, error } = await supabase
      .from('sites')
      .insert({ tenant_id: input.tenantId, name: input.name, subdomain: input.subdomain })
      .select()
      .single()
    if (error ?? !data) throw new Error(error?.message ?? 'Failed to create site')
    return toEntity(data)
  }

  async update(id: string, patch: Partial<Site>): Promise<Site> {
    const db: TablesUpdate<'sites'> = {}
    if (patch.name !== undefined) db.name = patch.name
    if (patch.published !== undefined) db.published = patch.published
    if (patch.settings !== undefined) db.settings = patch.settings as unknown as Json
    if (patch.customDomain !== undefined) db.custom_domain = patch.customDomain

    const { data, error } = await supabase.from('sites').update(db).eq('id', id).select().single()
    if (error ?? !data) throw new Error(error?.message ?? 'Failed to update site')
    return toEntity(data)
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('sites').delete().eq('id', id)
    if (error) throw new Error(error.message)
  }

  async countByTenantId(tenantId: string): Promise<number> {
    const { count, error } = await supabase
      .from('sites')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
    if (error) throw new Error(error.message)
    return count ?? 0
  }
}
